import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
//import { Pagination } from "./Pagination";
import emissionViewStore from "../../Store/marco/emissionViewStore";
import { getConstByStandard } from "../utils/getConstFileByStandard";
import { hasNumericValue } from "../utils/dataEntryCalculation";
import {
  parseMonthTimeIndex,
  timeIndexSortKey,
} from "../utils/reportingPeriod";
import dayjs from "dayjs";
//api
import { dataEntryAPI } from "../API/dataEntryAPI";

//mui-icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import UploadFileSharpIcon from "@mui/icons-material/UploadFileSharp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SimCardDownloadIcon from "@mui/icons-material/SimCardDownload";

//mui
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  Stack,
  Tooltip,
  LinearProgress,
  Chip,
} from "@mui/material";

//css
import styles from "../styles/dataEntry.module.css";

//翻譯
import { useTranslation } from "react-i18next";

//commonStore
import commonStore from "../../Store/marco/commonStore";

//modal
import DataEntryDeleteModal from "../modals/DataEntry/DataEntryDeleteModal";
import DataEntrySetUnitModal from "../modals/DataEntry/DataEntrySetUnitModal";
import DataEntryEditModal from "../modals/DataEntry/DataEntryEditModal";
import DataEntryUploadFilesModal from "../modals/DataEntry/DataEntryUploadFilesModal";
import DataEntryPreviewModal from "../modals/DataEntry/DataEntryPreviewModal";
import DataEntryAddAndSetUnitModal from "../modals/DataEntry/DataEntryAddAndSetUnitModal";

//tab欄位
const TAB_CONFIG = {
  "1.4 Fugitive Emissions": {
    hasFugitiveColumn: true,
    overrideTitle: {},
  },
};

function getTabConfig(tab) {
  return TAB_CONFIG[tab] ?? { hasFugitiveColumn: false, overrideTitle: {} };
}

// ✅ isReadOnly 判斷提升，避免在各子元件重複推導
function useIsReadOnly(access, hideAction) {
  return !access || hideAction;
}

//ActivityAccordion
const ActivityAccordion = observer(
  ({
    index,
    displayNumber,
    device,
    expanded,
    onExpand,
    access,
    t,
    hideAction,
  }) => {
    // ✅ isReadOnly 在這層算一次，往下傳
    const isReadOnly = useIsReadOnly(access, hideAction);
    const isFugitive =
      emissionViewStore.selectedTab === "1.4 Fugitive Emissions";

    const gridTemplateColumns = isReadOnly
      ? isFugitive
        ? "0.35fr 0.5fr 1.5fr 1.5fr repeat(4, 1fr)"
        : "0.35fr 0.5fr 1.5fr 1.5fr repeat(3, 1fr)"
      : isFugitive
        ? "0.35fr 0.5fr 1.5fr 1.5fr repeat(4, 1fr) 1.5fr"
        : "0.35fr 0.5fr 1.5fr 1.5fr repeat(3, 1fr) 1.5fr";
    return (
      <Accordion onChange={onExpand}>
        <AccordionSummary sx={{ padding: "8px 0px" }}>
          <Box
            display="grid"
            gridTemplateColumns={gridTemplateColumns}
            width="100%"
            alignItems="center"
            sx={{ textAlign: "center" }}
          >
            <ExpandMoreIcon
              color="primary"
              className={`transform transition-transform ${
                expanded ? "rotate-180" : "rotate-0"
              }`}
              sx={{ justifySelf: "center" }}
            />
            <ActivityInfo device={device} index={displayNumber} />

            {/**權限設定 */}
            {!isReadOnly &&
              (device.isFrozen ? (
                <Typography color="primary">
                  {t("Verifying data...")}
                </Typography>
              ) : (
                <ActionButtons device={device} />
              ))}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FilesData
            fileData={device.data}
            device={device}
            isReadOnly={isReadOnly}
          />
        </AccordionDetails>
      </Accordion>
    );
  },
);

//ActivityInfo
const ActivityInfo = observer(({ device, index }) => {
  const { t } = useTranslation("ActivityMain");
  const {
    itemName,
    totalConvertedValue,
    dataUnitName,
    dataTypeId,
    emissionFactorName,
    companyName,
    hierarchyNamePath,
  } = device;

  const dataTypeLabel =
    dataTypeId === 1
      ? "Month"
      : dataTypeId === 2
        ? "Year"
        : dataTypeId === 3
          ? "Day"
          : "Interval";

  return (
    <>
      <Typography>{index}</Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            justifyContent: "space-between",
            gap: "8px",
            width: "80%",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              color: "#333",
              fontWeight: 500,
              wordBreak: "break-word",
              textAlign: "center",
            }}
          >
            {companyName || "-"}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "13px",
            color: "#999",
            wordBreak: "break-word",
            textAlign: "left",
            width: "80%",
          }}
        >
          {t(hierarchyNamePath)}
        </Typography>
      </Box>

      <Typography>{itemName}</Typography>
      <Typography>{t(dataTypeLabel)}</Typography>

      {/* 如果類別是1.4，顯示冷媒名稱 */}
      {emissionViewStore.selectedTab === "1.4 Fugitive Emissions" && (
        <Typography>{emissionFactorName || "-"}</Typography>
      )}
      <Typography>{totalConvertedValue}</Typography>
      <Typography>{t(dataUnitName || "Unset")}</Typography>
    </>
  );
});

//操作按鈕的組件
const ActionButtons = ({ device }) => {
  const { t } = useTranslation("ActivityMain");

  // 判斷按鈕可不可以按
  const isAddButtonDisabled =
    device.dataTypeId === 2 &&
    device.data?.length > 0 &&
    device.data[0].datas?.length > 0;

  const isUnitUnset = device.dataUnitId === 0;

  const tooltipTitle = isAddButtonDisabled
    ? t("Yearly data can only have one entry.")
    : isUnitUnset
      ? t("Unit is not set.")
      : "";

  return (
    <Stack direction="row" spacing={2} justifyContent="flex-end">
      <Button
        variant="outlined"
        onClick={(event) => {
          event.stopPropagation(); //阻止Accordion的展開
          emissionViewStore._handleSetSelectedDataEntry(device);
          emissionViewStore._handleDataEntrySetUnitModal();
        }}
        sx={{ textTransform: "none" }}
      >
        {t("Data Setting")}
      </Button>
      <Tooltip
        title={tooltipTitle}
        disableHoverListener={!(isAddButtonDisabled || isUnitUnset)}
      >
        <span>
          <Button
            variant="contained"
            disabled={isAddButtonDisabled}
            onClick={(event) => {
              event.stopPropagation(); //阻止Accordion的展開
              emissionViewStore._handleSetSelectedDataEntry(device);
              emissionViewStore._handleSetIsShowDataEntryAddAndSetUnitModal(
                true,
              );
            }}
            sx={{ textTransform: "none" }}
          >
            {t("Add & Setting")}
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
};

const FilesData = ({ fileData, device, isReadOnly }) => {
  const { t } = useTranslation("ActivityMain");
  if (!fileData?.length) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <Typography>{t("No data!")}</Typography>
      </Box>
    );
  }

  // 先對數據進行排序（從小到大）
  const sortedFiles = [...fileData].sort(
    (a, b) => timeIndexSortKey(a.timeIndex) - timeIndexSortKey(b.timeIndex),
  );
  return sortedFiles.map((file, index) => (
    <FileAccordion
      key={index}
      file={file}
      device={device}
      isReadOnly={isReadOnly}
    />
  ));
};

const FileAccordion = observer(({ file, device, isReadOnly }) => {
  const { t } = useTranslation("ActivityMain");
  const [expanded, setExpanded] = useState(false);
  const store = emissionViewStore;
  const CONST = getConstByStandard(store.standardId);
  //取得指定data entry的檔案列表
  const getValidationFileList = async (dataEntryId) => {
    emissionViewStore._handleSetValidationFileListLoading(true);
    try {
      const scope = CONST.category[emissionViewStore.selectedTab].split("_")[0];

      // 呼叫 API 並直接取得處理好的陣列
      const dataArray = await dataEntryAPI.ValidationFileAPI(
        dataEntryId,
        scope,
      );
      console.log("拿到的佐證資料列表是：", dataArray);

      // 存入 MobX store
      emissionViewStore._handleSetValidationFileListData((prev) => {
        const newState = {
          ...prev,
          [dataEntryId]: dataArray,
        };
        return newState;
      });
    } catch (error) {
      console.error("Error fetching validation file list", error);
      emissionViewStore._handleSetValidationFileListData((prev) => ({
        ...prev,
        [dataEntryId]: [],
      }));
    } finally {
      emissionViewStore._handleSetValidationFileListLoading(false);
    }
  };

  // 處理手風琴展開/收合
  const handleChange = (event, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded && file.datas && file.datas.length > 0) {
      console.log("file.datas 存在，長度為:", file.datas.length);
      file.datas.forEach((data) => {
        // 只有在還沒有獲取過該數據的佐證資料時才獲取
        if (!emissionViewStore.validationFileListData[data.id]) {
          console.log("準備獲取佐證資料，數據ID:", data.id);
          getValidationFileList(data.id);
        } else {
          console.log("已存在佐證資料，跳過獲取，數據ID:", data.id);
        }
      });
    }
  };

  //將file.timeIndex ("2025-3") 轉換為對應的月份與年份
  const getTimeIndexLabel = (timeIndex) => {
    // 檢查是否為年份範圍格式（如：2024 ~ 2025）
    if (typeof timeIndex === "string" && timeIndex.includes("~")) {
      return timeIndex;
    }
    const parsed = parseMonthTimeIndex(timeIndex);
    if (!parsed) return emissionViewStore.selectedYear;
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = t(months[parsed.month - 1]);
    return parsed.year ? `${month} ${parsed.year}` : month;
  };

  return (
    <Accordion expanded={expanded} onChange={handleChange}>
      <AccordionSummary>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Stack direction="row" spacing={2}>
            {expanded ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
            <Typography>{getTimeIndexLabel(file.timeIndex)}</Typography>
          </Stack>
          <Typography>
            {t("Sub-Total")}: {file.totals}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <FileDataTable file={file} device={device} isReadOnly={isReadOnly} />
      </AccordionDetails>
    </Accordion>
  );
});
//轉換日期格式"YYYY-MM-DD_YYYY-MM-DD" 格式字串
function formatRangeDate(str) {
  // 處理範圍格式
  if (str.includes("_")) {
    const [startStr, endStr] = str.split("_");
    const start = dayjs(startStr);
    const end = dayjs(endStr);
    if (start.isValid() && end.isValid()) {
      return `${start.format("YYYY/MM/DD")}~${end.format("YYYY/MM/DD")}`;
    }
    return str;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    // 處理 YYYY-MM-DD
    const date = dayjs(str);
    return date.isValid() ? date.format("YYYY/MM/DD") : str;
  }
  if (/^\d{4}-\d{2}$/.test(str)) {
    // 處理 YYYY-MM
    const date = dayjs(str + "-01"); // dayjs 不支援只傳年月，所以補上日期
    return date.isValid() ? date.format("YYYY/MM") : str;
  }
  return str; // 其他格式：原樣回傳
}

//FileDataTable
const FILE_TABLE_TITLES = {
  cat3xId4: [
    "Used Date",
    "Origin",
    "Destination",
    "Distance",
    "Passenger",
    "Trip Type",
    "Number of Trips",
    "Validation File",
    "",
    "Consumption Value/Quantity",
    "Actions",
  ],
  cat3xId2: [
    "Used Date",
    "Origin",
    "Destination",
    "Trip Type",
    "Number of Trips",
    "Validation File",
    "",
    "Distance",
    "Actions",
  ],
  cat3x: [
    "Used Date",
    "Origin",
    "Destination",
    "Validation File",
    "",
    "Converted Operation Value",
    "Actions",
  ],
  cat3basic: [
    "Used Date",
    "Origin",
    "Destination",
    "Weight",
    "Distance",
    "Validation File",
    "",
    "Converted Operation Value",
    "Actions",
  ],
  default: [
    "Used Date",
    "Validation File",
    "",
    "Converted Operation Value",
    "Actions",
  ],
};

const FileDataTable = observer(({ file, device, isReadOnly }) => {
  const { t } = useTranslation("ActivityMain");
  const store = emissionViewStore;
  const CONST = getConstByStandard(store.standardId);
  const category = CONST.category;
  const cat = category[store.selectedTab].substring(3);

  const isCat3basic = ["3_1", "3_2"].includes(cat);
  const isCat3x = ["3_3", "3_4", "3_5"].includes(cat);
  const isCalculationId2 = device?.calculationId === 2 || false;
  const isCalculationId3 = device?.calculationId === 3 || false;
  const isCalculationId4 = device?.calculationId === 4 || false;

  const handleDownload = async (fileLink, fileName) => {
    try {
      // 使用 API 函數獲取檔案 blob
      const blob = await dataEntryAPI.HandleDownloadFileAPI(fileLink);

      // 創建 blob URL 並執行下載
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // 清理資源
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("下載失敗:", error);
    }
  };
  // 1. 動態計算表格標題
  const titleKey =
    isCat3x && isCalculationId4
      ? "cat3xId4"
      : isCat3x && isCalculationId2
        ? "cat3xId2"
        : isCat3x
          ? "cat3x"
          : isCat3basic
            ? "cat3basic"
            : "default";
  let fileTableTitles = FILE_TABLE_TITLES[titleKey];

  // isCat3basic 的單位從 dataUnitName (e.g. "tonne-km") 拆解
  // isCalculationId4 的單位從 dataUnitName (e.g. "Passenger-km", "Passenger-mile") 拆解
  const [weightUnit, splitDistanceUnit] = isCat3basic
    ? (device.dataUnitName ?? "").split("-")
    : isCat3x && isCalculationId4
      ? [undefined, (device.dataUnitName ?? "").split("-")[1]]
      : [];
  const distanceUnit =
    isCat3basic && isCalculationId2
      ? device.dataUnitName
      : splitDistanceUnit;

  // 若為唯讀模式，自動移除 "Actions" 欄位
  if (isReadOnly) {
    fileTableTitles = fileTableTitles.filter((title) => title !== "Actions");
  }
  //動態計算籃位寬度
  const totalParts = fileTableTitles.length + 1;
  const regularWidth = `${(1 / totalParts) * 100}%`;
  const doubleWidth = `${(1.5 / totalParts) * 100}%`;
  const halfWidth = `${(0.8 / totalParts) * 100}%`;

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {fileTableTitles.map((title, index) => {
            // 判斷當前標題是否為 Validation File
            const isValidationCol =
              title === "Validation File" ||
              title === "Converted Operation Value";
            const isHalfCol = title === "";

            return (
              <th
                key={index}
                style={{
                  width: isValidationCol
                    ? doubleWidth
                    : isHalfCol
                      ? halfWidth
                      : regularWidth,
                }}
              >
                {title ? t(title) : ""}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {/* 如果monthData存在 */}
        {file.datas &&
          file.datas.map((data) => (
            <tr key={data.id}>
              {/* Used Date (所有情況都有) */}
              <td scope="row">{formatRangeDate(data.usedDate)}</td>
              {/* Origin, Destination (isCat3x 或 isCat3basic 顯示) */}
              {(isCat3x || isCat3basic) && <td>{data.origin}</td>}
              {(isCat3x || isCat3basic) && <td>{data.destination}</td>}
              {/* Weight (僅 isCat3basic 顯示) */}
              {isCat3basic && (
                <td>
                  {!isCalculationId2 && !isCalculationId3 && (
                    <>
                      {data.weight}
                      <Chip
                        label={isCat3basic ? t(weightUnit) : ""}
                        sx={{
                          marginLeft: "8px",
                          height: 24,
                          bgcolor: "#e0f2fe",
                          color: "#1464c8",
                          borderRadius: "4px",
                          "& .MuiChip-label": {
                            padding: "4px 8px",
                          },
                        }}
                      />
                    </>
                  )}
                </td>
              )}
              {/* Distance 在佐證資料前 (isCat3basic 或 cat3xId2/4) */}
              {(isCat3basic || (isCat3x && isCalculationId4)) && (
                <td>
                  {(!isCalculationId2 || hasNumericValue(data.distance)) && (
                    <>
                      {data.distance}
                      {distanceUnit && (
                        <Chip
                          label={t(distanceUnit)}
                          sx={{
                            marginLeft: "8px",
                            height: 24,
                            bgcolor: "#e0f2fe",
                            color: "#1464c8",
                            borderRadius: "4px",
                            "& .MuiChip-label": {
                              padding: "4px 8px",
                            },
                          }}
                        />
                      )}
                    </>
                  )}
                </td>
              )}
              {/* Passenger (僅 cat3xId4 顯示) */}
              {isCat3x && isCalculationId4 && <td>{data.passengerCount}</td>}
              {/* Trip Type / Number of Trips (cat3xId2 or cat3xId4) */}
              {isCat3x && (isCalculationId2 || isCalculationId4) && (
                <td>
                  {data.tripType === 1
                    ? t("One Way")
                    : data.tripType === 2
                    ? t("Round Trip")
                    : ""}
                </td>
              )}
              {isCat3x && (isCalculationId2 || isCalculationId4) && (
                <td>{data.trips}</td>
              )}
              {/* 佐證資料欄位 */}
              {/* 檔名的欄位 */}
              <td>
                {emissionViewStore.validationFileListLoading ? (
                  <LinearProgress sx={{ width: "100px" }} />
                ) : (
                  emissionViewStore.validationFileListData[data.id]?.map(
                    (validationFile, index) => (
                      <div key={validationFile.id || index}>
                        {validationFile.fileName}.{validationFile.extension}
                        <br />
                      </div>
                    ),
                  )
                )}
              </td>
              {/* Icon 的欄位 */}
              <td>
                {!emissionViewStore.validationFileListLoading &&
                  emissionViewStore.validationFileListData[data.id]?.map(
                    (validationFile, index) => (
                      <div key={validationFile.id || index}>
                        {validationFile.extension?.toLowerCase() !== "zip" &&
                          validationFile.extension?.toLowerCase() !== "rar" && (
                            <Tooltip title={t("Preview")} arrow>
                              <VisibilityIcon
                                color="primary"
                                sx={{ cursor: "pointer" }}
                                onClick={() => {
                                  emissionViewStore._handleDataEntryPreviewModal();
                                  emissionViewStore._handleSetDataEntryPreviewInfo(
                                    validationFile.path,
                                    validationFile.fileName,
                                    validationFile.extension,
                                  );
                                }}
                              />
                            </Tooltip>
                          )}

                        {/* 下載 Icon */}
                        <Tooltip title={t("Download")} arrow>
                          <SimCardDownloadIcon
                            color="success"
                            sx={{ cursor: "pointer", ml: 1 }}
                            onClick={() =>
                              handleDownload(
                                validationFile.path,
                                validationFile.fileName,
                                validationFile.extension,
                              )
                            }
                          />
                        </Tooltip>
                        <br />
                      </div>
                    ),
                  )}
              </td>
              {/* Consumption Value / Converted Operation Value */}
              <td>
                {data?.convertedOperationValue != null
                  ? Number(data.convertedOperationValue).toFixed(4)
                  : "-"}
                <Chip
                  label={t(device.dataUnitName)}
                  sx={{
                    marginLeft: "8px",
                    height: 24,
                    bgcolor: "#e0f2fe",
                    color: "#1464c8",
                    borderRadius: "4px",
                    "& .MuiChip-label": {
                      padding: "4px 8px",
                    },
                  }}
                />
              </td>
              {!isReadOnly && (
                <td>
                  {device.isFrozen ? (
                    <Typography color="primary">
                      {t("Verifying data...")}
                    </Typography>
                  ) : data.isDirectApiEntry ? (
                    ""
                  ) : (
                    <div className={styles.actions}>
                      <Tooltip title={t("Upload")} arrow>
                        <UploadFileSharpIcon
                          color="primary"
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            emissionViewStore._handleSetSelectedDataEntry(
                              device,
                            ); // 活動資訊
                            emissionViewStore._handleSetSelectedDataEntryItem({
                              ...data,
                              validationFiles:
                                emissionViewStore.validationFileListData[
                                  data.id
                                ],
                            });
                            {
                              console.log(
                                "validationFileListData",
                                emissionViewStore.validationFileListData[
                                  data.id
                                ],
                              );
                            }
                            emissionViewStore._handleDataEntryUploadFilesModal();
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={t("Edit")} arrow>
                        <DriveFileRenameOutlineIcon
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            emissionViewStore._handleSetSelectedDataEntry(
                              device,
                            ); // 活動資訊
                            emissionViewStore._handleSetSelectedDataEntryItem(
                              data,
                            );
                            emissionViewStore._handleDataEntryEditModal();
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={t("Delete")} arrow>
                        <DeleteForeverIcon
                          color="warning"
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            emissionViewStore._handleSetSelectedDataEntry(
                              device,
                            ); // 活動資訊
                            emissionViewStore._handleSetSelectedDataEntryItem(
                              data,
                            );
                            emissionViewStore._handleDataEntryDeleteModal();
                          }}
                        />
                      </Tooltip>
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
      </tbody>
    </table>
  );
});

//main

export const DataEntry = observer(() => {
  const { authComArr } = emissionViewStore;
  const { t } = useTranslation("ActivityMain"); //之後要改
  const { access } = commonStore;
  const store = emissionViewStore;
  const cmStore = commonStore;

  const scope = cmStore?.EACategory?.find(
    (objects) => objects.label === store.selectedScope,
  );
  ////////找出是否要渲染Action
  const hideAction = scope?.category?.find(
    (item) => item.label === store.selectedTab,
  )?.isDisabled;

  const isReadOnly = useIsReadOnly(access, hideAction);

  const { hasFugitiveColumn, overrideTitle } = getTabConfig(
    emissionViewStore.selectedTab,
  );

  const baseTitles = isReadOnly
    ? ["", "No.", "Entity Name", "Name", "Time Format", "Total Amount", "Unit"]
    : [
        "",
        "No.",
        "Entity Name",
        "Name",
        "Time Format",
        "Total Amount",
        "Unit",
        "Actions",
      ];

  //依照tabs不同判斷不同標題
  const activityAccordionTitles = baseTitles
    .map((title, i) => overrideTitle[i] ?? title)
    .reduce((acc, title, i) => {
      if (i === 5 && hasFugitiveColumn) acc.push("Emission Gas Type");
      acc.push(title);
      return acc;
    }, []);

  const isFugitive = emissionViewStore.selectedTab === "1.4 Fugitive Emissions";

  const [expandedPanels, setExpandedPanels] = useState({});

  const handleChange = (panel) => (_, isExpanded) => {
    setExpandedPanels((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  ////////初始Data Entry List
  useEffect(() => {
    if (hideAction) return; //如果未揭漏不去呼叫API
    emissionViewStore.getDEListData({ keepPage: true });
  }, [
    emissionViewStore.selectedTab,
    emissionViewStore.isRefresh,
    emissionViewStore.selectedYear,
    authComArr,
    emissionViewStore.searchKeyword,
    emissionViewStore.selectedScope,
  ]);

  return (
    <div className={styles.container}>
      <div
        className={`${styles.gridContainer} ${
          isReadOnly
            ? isFugitive
              ? styles.gridColumnsExtraViewer
              : styles.gridColumnsDefaultViewer
            : isFugitive
              ? styles.gridColumnsExtra
              : styles.gridColumnsDefault
        }`}
      >
        {activityAccordionTitles.map((title, index) => (
          <div key={index} className={styles.gridItem}>
            {t(title)}
          </div>
        ))}
      </div>

      {/* 未揭露此活動 - 最優先判斷 */}
      {hideAction ? (
        <div className={styles.noData}>
          <p>{t("This activity has not been disclosed")}</p>
        </div>
      ) : (
        <>
          {/* 沒資料筆數 */}
          {!emissionViewStore.dataEntryListLoading &&
            (!emissionViewStore.DECurrentPageList ||
              emissionViewStore.DECurrentPageList.length === 0) && (
              <div className={styles.noData}>
                <p>
                  {emissionViewStore.dataEntryListError ===
                  "REPORTING_PERIOD_NOT_SET"
                    ? t(
                        "Reporting period is not set. Please complete the boundary settings first.",
                      )
                    : t("No data!")}
                </p>
              </div>
            )}

          {/* 資料列表 */}
          {emissionViewStore.DECurrentPageList.map((item, index) => (
            <ActivityAccordion
              key={item.id || index}
              index={index}
              displayNumber={
                (emissionViewStore.dataEntryListData.page - 1) *
                  emissionViewStore.dataEntryListData.pageSize +
                index +
                1
              }
              device={item}
              expanded={expandedPanels[`panel-${index}`]}
              onExpand={handleChange(`panel-${index}`)}
              access={access}
              hideAction={hideAction}
              t={t}
            />
          ))}
        </>
      )}

      {/* <Pagination /> */}
      <DataEntryUploadFilesModal />

      <DataEntryEditModal />
      <DataEntryPreviewModal />
      <DataEntrySetUnitModal />
      <DataEntryAddAndSetUnitModal />
      <DataEntryDeleteModal />
    </div>
  );
});
